/**
 * «Hvem er med?» — lesingene og den ene skrivingen (QA13 punkt 32).
 *
 * Tre spørsmål med samme svarform: «Ny festival» (ingen forslag), «Ny årgang» (forslaget fra
 * forrige årgang) og «Legg til person» (hvem som alt er med). `CACHE.list`: et medlem som legges
 * til i huset, skal stå der neste gang dialogen åpnes.
 */
import {
  addEditionMembersResponseSchema,
  memberChoicesResponseSchema,
  type AddEditionMembersRequest,
  type AddEditionMembersResponse,
  type MemberChoicesResponse,
} from '@tvitvi/shared';
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

export type MemberChoiceScope = 'new-festival' | { festivalId: string } | { editionId: string };

function pathFor(scope: MemberChoiceScope): string {
  if (scope === 'new-festival') return '/member-choices';
  if ('festivalId' in scope) return `/festivals/${scope.festivalId}/member-choices`;
  return `/editions/${scope.editionId}/member-choices`;
}

export function memberChoicesQueryOptions(orgId: string, scope: MemberChoiceScope) {
  return queryOptions({
    queryKey: qk.memberChoices(orgId, scope),
    queryFn: ({ signal }): Promise<MemberChoicesResponse> =>
      apiRequest({ path: pathFor(scope), schema: memberChoicesResponseSchema, signal }),
    ...CACHE.list,
  });
}

export interface AddEditionMembersInput {
  editionId: string;
  body: AddEditionMembersRequest;
}

/**
 * Flere medlemmer inn i årgangen på én gang. Feier det samme som `useCreateParticipation`: Personer,
 * vaktplanen og frivillige står ved siden av hverandre og handler om de samme menneskene.
 */
export function useAddEditionMembers(
  orgId: string,
): UseMutationResult<AddEditionMembersResponse, unknown, AddEditionMembersInput> {
  return useMutation({
    mutationFn: ({ editionId, body }: AddEditionMembersInput) =>
      apiRequest({
        path: `/editions/${editionId}/members`,
        method: 'POST',
        body,
        schema: addEditionMembersResponseSchema,
      }),
    meta: { invalidates: [qk.participations(orgId), qk.crew(orgId), qk.volunteers(orgId)] },
  });
}
